"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslations } from "next-intl";
import { useAuth } from "@/contexts/AuthContext";
import type { OtrosProyectos } from "@/types/models/otros";
import { otrosService } from "@/lib/services/otros.service";

export interface OtrosProyectosData {
  otrosProyectos: OtrosProyectos[];
  isFetching: boolean;
  error: unknown;
  refetchAll: () => Promise<void>;
  deleteOtrosProyecto: (id: number) => Promise<void>;
}

export function useOtrosProyectosData(): OtrosProyectosData {
  const { isLoggedIn, currentTenantId, userLang } = useAuth();
  const t = useTranslations();

  const [otrosProyectos, setOtrosProyectos] = useState<OtrosProyectos[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const canFetch = isLoggedIn && !!currentTenantId;
  const tenantId = currentTenantId ? Number(currentTenantId) : null;

  const toastMessages = useMemo(
    () => ({
      fetchErrorTitle: t("toast.otros-proyectos.fetch-error-title"),
      fetchErrorDescription: t("toast.otros-proyectos.fetch-error-desc"),
      successTitle: t("toast.success-title"),
      errorTitle: t("toast.error-title"),
      deleteSuccess: t("messages.deleteSuccess"),
      deleteError: t("messages.deleteError"),
    }),
    [t]
  );

  const showFetchErrorToast = useCallback(() => {
    toast.error(toastMessages.fetchErrorTitle, {
      description: toastMessages.fetchErrorDescription,
      descriptionClassName: "!text-gray-800 dark:!text-gray-200",
    });
  }, [toastMessages]);

  const showDeleteSuccessToast = useCallback(() => {
    toast.success(toastMessages.successTitle, {
      description: toastMessages.deleteSuccess,
      descriptionClassName: "!text-gray-800 dark:!text-gray-200",
    });
  }, [toastMessages]);

  const showDeleteErrorToast = useCallback(() => {
    toast.error(toastMessages.errorTitle, {
      description: toastMessages.deleteError,
      descriptionClassName: "!text-gray-800 dark:!text-gray-200",
    });
  }, [toastMessages]);

  const refetchAll = useCallback(async () => {
    if (!canFetch || !tenantId) return;

    setIsFetching(true);
    setError(null);

    try {
      const data = await otrosService.fetchOtrosProyectosByTenant({
        tenantId,
        userLang,
      });

      setOtrosProyectos(data);
    } catch (err) {
      setError(err);
      showFetchErrorToast();
    } finally {
      setIsFetching(false);
    }
  }, [canFetch, tenantId, userLang, showFetchErrorToast]);

  const deleteOtrosProyecto = useCallback(
    async (id: number) => {
      if (!canFetch) return;

      try {
        await otrosService.deleteOtroProyecto(id);
        await refetchAll();
        showDeleteSuccessToast();
      } catch (err) {
        showDeleteErrorToast();
        throw err;
      }
    },
    [canFetch, refetchAll, showDeleteSuccessToast, showDeleteErrorToast]
  );

  useEffect(() => {
    if (!canFetch || !tenantId) {
      setOtrosProyectos([]);
      setError(null);
      return;
    }

    void refetchAll();
  }, [canFetch, tenantId, refetchAll]);

  return useMemo(
    () => ({
      otrosProyectos,
      isFetching,
      error,
      refetchAll,
      deleteOtrosProyecto,
    }),
    [otrosProyectos, isFetching, error, refetchAll, deleteOtrosProyecto]
  );
}
